import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Alert, AvailabilityCalendarModal } from '@uplift/ui-v2';
import { useCalendarStore, useCurrentuser } from '@uplift/contexts';
import { CalendarEditView, ProviderMedicalRole, TimeRange } from '@uplift/types';

import { Accordion, AccordionDetails, AccordionSummary } from '../SidebarAccordion.component';
import { getAvailabilityTemplate, setAvailabilityTemplate, userAvailabilityForRange } from '@uplift/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EditTimeDropdown } from './EditTimeDropdown.component';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { AvailabilityOverrideModal } from '../AvailabilityOverrideModal';
import { DateObject } from '@uplift/services';
import { providerScheduleViewTypeRoute } from '@uplift/constants';

export type SidebarAvailabilityProps = {
  handleAvailabilityDateOverride: (date: DateObject, ranges: TimeRange[]) => Promise<void>;
};

export const SidebarAvailability: React.FC<SidebarAvailabilityProps> = observer((props) => {
  const initialEditView = providerScheduleViewTypeRoute.useQueryParam('editView');
  const [ editModal, setEditModal ] = React.useState<CalendarEditView | null>(initialEditView ?? null);
  const calendarStore = useCalendarStore();
  const me = useCurrentuser();

  const onEditModalClose = React.useCallback(() => setEditModal(null), []);

  const queryClient = useQueryClient();
  const { data, isInitialLoading: isLoading } = useQuery({
    queryKey: [ 'availabilityTemplate', me.id ],
    queryFn: ({ queryKey: [ _, _userId ] }) => {
      return getAvailabilityTemplate();
    },
    enabled: editModal === CalendarEditView.recurringHours,
  });

  const recurringEditMode = React.useMemo(() => {
    if (calendarStore?.availabilityTemplate && Object.values(calendarStore?.availabilityTemplate).flat().length === 0) {
      return 'add';
    }

    return 'edit';
  }, [ calendarStore.availabilityTemplate ]);

  return (
    <>
      <Accordion elevation={0} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography style={{ fontWeight: 600 }}>Matching Availability</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={12}>
              <EditTimeDropdown
                recurringEditMode={recurringEditMode}
                onEditRecurring={() => setEditModal(CalendarEditView.recurringHours)}
                onEditAvailability={() => setEditModal(CalendarEditView.specificDate)}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="body2">Times available to new clients when booking with you for the first time</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <AvailabilityCalendarModal
        open={editModal === CalendarEditView.recurringHours}
        loading={isLoading}
        onClose={onEditModalClose}
        availabilityTemplate={data?.days ?? {}}
        onAvailabilityTemplateChange={async (newTemplate) => {
          const result = await setAvailabilityTemplate({ days: newTemplate });

          queryClient.setQueryData([ 'availabilityTemplate', me.id ], result);
          const { overrides, template } = await userAvailabilityForRange({
            datetime_from: calendarStore._lastInfo.startStr,
            datetime_to: calendarStore._lastInfo.endStr,
          });

          runInAction(() => {
            calendarStore.availabilityOverrides = overrides;
            calendarStore.availabilityTemplate = template;
          });
        }}
        warningOnLessThenHour={me.medicalRole === ProviderMedicalRole.therapist}
      />
      <AvailabilityOverrideModal
        open={editModal === CalendarEditView.specificDate}
        onClose={onEditModalClose}
        onAvailabilityDateOverride={props.handleAvailabilityDateOverride}
        prepend={(
          <Box mb={3}>
            <Alert severity="info">
              <Box display="flex">
                Previously booked sessions will not be canceled
              </Box>
            </Alert>
          </Box>
        )}
      />
    </>
  );
});
